import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPersonalInfo, PersonalInfo } from '../data/contentService';
import SocialLinks from '../components/SocialLinks';
import './Home.css';

const Home: React.FC = () => {
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo | null>(null);

  useEffect(() => {
    const loadPersonalInfo = async () => {
      try {
        const info = await fetchPersonalInfo();
        console.log('Loaded personal info:', info); // Debug log
        setPersonalInfo(info);
      } catch (error) {
        console.error('Error loading personal info:', error);
      }
    };

    loadPersonalInfo();
  }, []);

  return (
    <div className="home">
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>About me</h1>
              <p className="hero-subtitle">
                {personalInfo?.title || 'Research Scientist'} at {personalInfo?.affiliation || 'TNO'}, {personalInfo?.location || 'Netherlands'}
              </p>
              <p className="hero-description">
                {personalInfo?.bio || 'I am passionate about advancing artificial intelligence research and its applications in real-world scenarios.'}
              </p>
              
              
              <div className="hero-actions">
                <Link to="/publications" className="btn btn-primary">
                  View Publications
                </Link>
                <Link to="/contact" className="btn btn-secondary">
                  Get in Touch
                </Link>
                {personalInfo?.cv && (
                  <a href={personalInfo.cv} className="btn btn-outline" target="_blank" rel="noopener noreferrer">
                    Download CV
                  </a>
                )}
              </div>
            </div>
            <div className="hero-image">
              {personalInfo?.profileImage ? (
                <img 
                  src={personalInfo.profileImage} 
                  alt={personalInfo.name || 'Profile'} 
                  className="profile-image"
                />
              ) : (
                <div className="placeholder-image">
                  <span>Profile Photo</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="about-preview">
        <div className="container">
          <h2>About My Research</h2>
          <div className="research-areas">
            {personalInfo?.interests ? (
              personalInfo.interests.map((interest, index) => {
                const getDescription = (interest: string) => {
                  switch (interest) {
                    case 'Artificial Intelligence':
                      return 'Developing innovative AI solutions and advancing research in artificial intelligence.';
                    case 'Decision Support':
                      return "As part of TNO's Intelligence & Decision Support team, I research how we can use AI and other technologies to help people make better decisions.";
                    case 'Human-AI Interaction':
                      return "I'm interested in how people can effectively interact with and understand AI systems — from explainable decision support interfaces to agentic frameworks that help users work with AI to structure and accomplish complex tasks.";
                    default:
                      return 'I work on developing new solutions and advancing research in the AI field.';
                  }
                };
                
                return (
                  <div key={index} className="research-area">
                    <h3>{interest}</h3>
                    <p>{getDescription(interest)}</p>
                  </div>
                );
              })
            ) : (
              <>
                <div className="research-area">
                  <h3>Machine Learning</h3>
                  <p>Developing novel algorithms and approaches for complex learning problems.</p>
                </div>
                <div className="research-area">
                  <h3>AI Applications</h3>
                  <p>Creating practical AI solutions for industry and society.</p>
                </div>
                <div className="research-area">
                  <h3>Research Innovation</h3>
                  <p>Bridging the gap between academic research and real-world implementation.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {personalInfo?.social && (
        <section className="social-section">
          <div className="container">
            <h2>Connect With Me</h2>
            <p>Contact me for collaborations, questions, or discussions.</p>
            <SocialLinks social={personalInfo.social} />
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;

